import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
} from '@mui/material';
import React, { memo, useEffect, useState } from 'react';
import { ArrowDownIcon, CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { BaseRow, BaseSelect } from '~/components/common';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '~/redux/hooks';

import { IJobStatus } from '~/utils/interface/job-status';
import { handleCreateJobStatus, handleEditJobStatus } from '~/thunks/job-status.ts/jobStatusThunk';
import { jobStatusActions } from '~/thunks/job-status.ts/jobStatusSlice';

import classNames from 'classnames/bind';
import styles from './EditJobStatusDialog.module.scss';
import { HexColorPicker } from 'react-colorful';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import { MASTER_DATA_JOB_STATUS_CODES } from '~/utils/constants/master';

const cx = classNames.bind(styles);

type EditJobStatusDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  setOpenDrawer?: (open: boolean) => void;
  mode?: string;
  title?: string;
  data?: IJobStatus;
  children?: React.ReactNode;
};

const initialValues: IJobStatus = {
  name: '',
  color: '',
  backgroundColor: '',
  code: '',
};

const EditJobStatusDialog = (props: EditJobStatusDialogProps) => {
  const { mode = DIALOG_MODE_CREATE, data } = props;
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [colorPickerBGOpen, setColorPickerBGOpen] = useState(false);
  const { t } = useTranslation(['admin/job-status']);
  const dispatch = useAppDispatch();
  const { jobStatusCodes } = useAppSelector((state) => state.master);

  const { jobStatusId } = useAppSelector((state) => state.jobStatus);
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleCreateJobStatus(values)).unwrap();
          dispatch(jobStatusActions.changeNeedRefreshData(true));
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
      if (mode === DIALOG_MODE_EDIT) {
        try {
          await dispatch(handleEditJobStatus({ id: jobStatusId, params: values })).unwrap();
          dispatch(jobStatusActions.changeNeedRefreshData(true));
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });
  useEffect(() => {
    dispatch(
      handleGetMasterData({
        type: [MASTER_DATA_JOB_STATUS_CODES],
      })
    );
  }, [dispatch]);
  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT) {
      form.setFieldValue('name', cast(data?.name));
      form.setFieldValue('color', cast(data?.color));
      form.setFieldValue('code', cast(data?.code));
      form.setFieldValue('backgroundColor', cast(data?.backgroundColor));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, data]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    setColorPickerBGOpen(false);
    setColorPickerOpen(false);
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
    if (props.setOpenDrawer) {
      props.setOpenDrawer(open);
    }
  };

  const handleCancel = () => {
    handleSetOpen(false);
    form.resetForm();
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('addNew') : t('edit')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('properties.name')}</InputLabel>
              <TextField
                name='name'
                placeholder={t('properties.name')}
                value={form.values?.name}
                onChange={form.handleChange}
                error={Boolean(form.errors?.name)}
                helperText={form.errors?.name}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('properties.code')}</InputLabel>
              <BaseSelect
                name='code'
                value={form.values?.code}
                placeholder={t('properties.code')}
                options={jobStatusCodes}
                onChange={form.handleChange}
                error={Boolean(form.errors?.code)}
                helperText={form.errors?.code}
              ></BaseSelect>
            </Box>
          </BaseRow>

          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('properties.color')}</InputLabel>
              <Box display='flex' alignItems='center' gap='10px' className='flex-row'>
                <Box
                  className='w-[20px] h-[20px] rounded-full border border-solid border-[#ccc]'
                  style={{ backgroundColor: form.values.color }} // Dynamic style
                />
                <TextField
                  name='color'
                  value={form.values.color}
                  onChange={(e) => form.setFieldValue('color', e.target.value)} // Update form state
                  placeholder='#000000'
                  inputProps={{ readOnly: true }}
                  error={Boolean(form.errors.color)}
                  helperText={form.errors.color}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton onClick={() => setColorPickerOpen(!colorPickerOpen)}>
                          <ArrowDownIcon className={cx('small-icon')} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              {colorPickerOpen && (
                <Box className='my-[10px]'>
                  <HexColorPicker
                    color={form.values.color}
                    onChange={(newColor) => form.setFieldValue('color', newColor)}
                  />
                </Box>
              )}
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('properties.backgroundColor')}</InputLabel>
              <Box display='flex' alignItems='center' gap='10px' className='flex-row'>
                <Box
                  className='w-[20px] h-[20px] rounded-full border border-solid border-[#ccc]'
                  style={{ backgroundColor: form.values.backgroundColor }} // Dynamic style
                />
                <TextField
                  name='color'
                  value={form.values.backgroundColor}
                  onChange={(e) => form.setFieldValue('color', e.target.value)} // Update form state
                  placeholder='#000000'
                  inputProps={{ readOnly: true }}
                  error={Boolean(form.errors.backgroundColor)}
                  helperText={form.errors.backgroundColor}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton onClick={() => setColorPickerBGOpen(!colorPickerBGOpen)}>
                          <ArrowDownIcon className={cx('small-icon')} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              {colorPickerBGOpen && (
                <Box className='my-[10px]'>
                  <HexColorPicker
                    color={form.values.backgroundColor}
                    onChange={(newColor) => form.setFieldValue('backgroundColor', newColor)}
                  />
                </Box>
              )}
            </Box>
          </BaseRow>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={handleCancel}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditJobStatusDialog);
